import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Wrench, AlertTriangle, ChevronRight, Bell, Check, Phone, User, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { apiService } from '../../services/api';

export const AlertaManutencaoGlobal = () => {
  const [alertas, setAlertas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertaExpandido, setAlertaExpandido] = useState(null);
  const [confirmandoId, setConfirmandoId] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // 🆕 No mobile, o ícone bottom-right sobrepõe o botão "Enviar" do chat
  // de Atendimentos. Não renderiza nessa rota — o admin pode acessar os
  // alertas pelo menu lateral normalmente.
  const ocultarPorRota =
    typeof window !== 'undefined' &&
    window.innerWidth < 1024 &&
    /\/admin\/atendimentos/i.test(location.pathname || '');

  // Carregar informações do usuário
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setUser(JSON.parse(userStr));
      } catch (e) {
        console.error('Erro ao parsear user:', e);
      }
    }
  }, []);

  const carregarAlertas = useCallback(async () => {
    // Não carregar alertas se for sub-admin
    if (user?.tipo === 'sub_admin') {
      return;
    }
    try {
      const response = await apiService.api.get('/alertas/manutencao-proxima');
      setAlertas(response.data.alertas || []);
    } catch (error) {
      console.error('Erro ao carregar alertas:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user && user.tipo !== 'sub_admin') {
      carregarAlertas();
      // Atualizar a cada 2 minutos
      const interval = setInterval(carregarAlertas, 120000);
      return () => clearInterval(interval);
    }
  }, [carregarAlertas, user]);

  const confirmarNotificacao = async (motoId) => {
    setConfirmandoId(motoId);
    try {
      await apiService.api.post(`/alertas/manutencao-proxima/${motoId}/confirmar-notificacao`);
      // Remover alerta da lista
      setAlertas(prev => prev.filter(a => a.moto_id !== motoId));
      setAlertaExpandido(null);
    } catch (error) {
      console.error('Erro ao confirmar notificação:', error);
    } finally {
      setConfirmandoId(null);
    }
  };

  const abrirWhatsApp = (alerta) => {
    if (!alerta.cliente_telefone) return;
    
    const telefone = alerta.cliente_telefone.replace(/\D/g, '');
    const telefoneFormatado = telefone.startsWith('55') ? telefone : `55${telefone}`;
    
    let mensagem;
    
    mensagem = `🔧 *TROCA DE ÓLEO* 🔧

Olá *${alerta.cliente_nome}*,

A moto *${alerta.moto_placa}* já está no km para *troca de óleo*.

📊 KM Atual: *${alerta.km_atual.toLocaleString()} km*

━━━━━━━━━━━━━━━
*AUTORIZAÇÃO NECESSÁRIA*
━━━━━━━━━━━━━━━

Por favor, *autorize a troca de óleo* e compareça no endereço abaixo:

📍 *Av Kennedy 4845, Vila Tupi - Praia Grande*

Aguardamos sua confirmação!

_Desloca Locação_`;

    const mensagemEncoded = encodeURIComponent(mensagem);
    window.open(`https://wa.me/${telefoneFormatado}?text=${mensagemEncoded}`, '_blank');
  };

  const isFrotaMecanico = (alerta) => {
    const nome = (alerta.cliente_nome || '').toUpperCase();
    return nome.includes('CARLOS HUMBERTO');
  };

  const notificarMecanico = (alerta) => {
    const plano = (alerta.tipo_plano || '').toLowerCase();
    const planoLabel = plano === 'fidelidade' ? 'FIDELIDADE' : plano === 'anual' ? 'ANUAL' : 'MENSAL';
    const frotaMecanico = isFrotaMecanico(alerta);

    let mensagem;

    if (frotaMecanico) {
      // Moto da frota do mecânico — regras especiais
      mensagem = `🔧 *ORDEM DE SERVIÇO - MANUTENÇÃO* 🔧

*Cliente:* ${alerta.cliente_nome}
*Plano:* ${planoLabel}
*Moto:* ${alerta.moto_placa} - ${alerta.moto_modelo || 'DK 160'}
*KM Atual:* ${alerta.km_atual?.toLocaleString() || 'N/A'} km

━━━━━━━━━━━━━━━
⚠️ *MOTO DA FROTA DO MECÂNICO*
━━━━━━━━━━━━━━━

*Regras de cobrança:*
• *Mão de obra:* SEM COBRANÇA
• *Óleo:* 100% por conta da LOCADORA
• *Peças (se houver troca):* Despesa dividida 50/50 (Locadora / Mecânico)

━━━━━━━━━━━━━━━

📋 *Verificar no checklist:*
• Nível e troca de óleo
• Filtro de ar
• Corrente/relação
• Freios (pastilha/lona)
• Pneus (calibragem e desgaste)
• Faróis e sinalização

⚠️ Conferir KM no painel da moto antes de iniciar.
Em caso de troca de peças, informar valor para aprovação antes de executar.

_Desloca Locação_`;
    } else {
      // Moto padrão — regras por plano
      let regrasPlano = '';
      if (plano === 'fidelidade') {
        regrasPlano = `*PLANO FIDELIDADE* — Manutenção por conta da LOCADORA
Realizar manutenção completa conforme checklist.`;
      } else {
        regrasPlano = `*PLANO ${planoLabel}* — Cobertura inclui SOMENTE troca de óleo.
Demais itens são de responsabilidade do cliente.`;
      }

      mensagem = `🔧 *ORDEM DE SERVIÇO - MANUTENÇÃO* 🔧

*Cliente:* ${alerta.cliente_nome}
*Plano:* ${planoLabel}
*Moto:* ${alerta.moto_placa} - ${alerta.moto_modelo || 'DK 160'}
*KM Atual:* ${alerta.km_atual?.toLocaleString() || 'N/A'} km

━━━━━━━━━━━━━━━
${regrasPlano}
━━━━━━━━━━━━━━━

📋 *Verificar no checklist:*
• Nível e troca de óleo
• Filtro de ar
• Corrente/relação
• Freios (pastilha/lona)
• Pneus (calibragem e desgaste)
• Faróis e sinalização

⚠️ Conferir KM no painel da moto antes de iniciar.

_Desloca Locação_`;
    }

    const mensagemEncoded = encodeURIComponent(mensagem);
    window.open(`https://wa.me/5513998055527?text=${mensagemEncoded}`, '_blank');
  };

  // Não renderizar nada se for sub-admin
  if (user?.tipo === 'sub_admin') return null;

  if (alertas.length === 0) return null;

  // No mobile + página Atendimentos: oculta para não tampar botão Enviar do chat.
  if (ocultarPorRota) return null;

  return (
    <>
      {/* Badge flutuante de alertas */}
      <div className="fixed bottom-4 right-4 z-50 lg:bottom-6 lg:right-6">
        <button
          onClick={() => setAlertaExpandido(alertaExpandido ? null : 'lista')}
          className="relative bg-orange-500 hover:bg-orange-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 animate-pulse hover:animate-none"
          data-testid="alerta-manutencao-btn"
        >
          <Wrench className="h-6 w-6" />
          <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
            {alertas.length}
          </span>
        </button>
      </div>

      {/* Painel de alertas expandido */}
      {alertaExpandido && (
        <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center p-4 bg-black/50">
          <div 
            className="bg-slate-800/90 rounded-t-2xl lg:rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom duration-300"
            data-testid="painel-alertas-manutencao"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-slate-800/90/20 p-2 rounded-full">
                    <Bell className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Alertas de Manutenção</h3>
                    <p className="text-orange-100 text-sm">{alertas.length} moto(s) próxima(s) da troca</p>
                  </div>
                </div>
                <button
                  onClick={() => setAlertaExpandido(null)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Lista de alertas */}
            <div className="overflow-y-auto max-h-[60vh] p-4 space-y-3">
              {alertas.map((alerta) => (
                <div
                  key={alerta.moto_id}
                  className={`border rounded-xl overflow-hidden transition-all ${
                    alerta.urgencia === 'ATRASADO'
                      ? 'border-red-400 bg-red-100'
                      : alerta.urgencia === 'CRÍTICO' 
                        ? 'border-red-300 bg-red-50' 
                        : 'border-orange-200 bg-orange-50'
                  }`}
                >
                  {/* Info da moto */}
                  <div className="p-4">
                    {/* Badge interno para admin - Plano Anual */}
                    {alerta.responsabilidade_cliente && (
                      <div className="mb-3 flex items-center gap-2 bg-blue-100 border border-blue-300 rounded-lg px-3 py-2" data-testid={`badge-cliente-${alerta.moto_id}`}>
                        <User className="h-4 w-4 text-blue-600 flex-shrink-0" />
                        <span className="text-xs font-bold text-blue-700 uppercase">
                          Manutenção por conta do Cliente (Plano Anual)
                        </span>
                      </div>
                    )}
                    {/* Badge Frota Mecânico */}
                    {isFrotaMecanico(alerta) && (
                      <div className="mb-3 flex items-center gap-2 bg-amber-100 border-2 border-amber-400 rounded-lg px-3 py-2" data-testid={`badge-frota-mecanico-${alerta.moto_id}`}>
                        <Wrench className="h-4 w-4 text-amber-600 flex-shrink-0" />
                        <span className="text-xs font-bold text-amber-700 uppercase">
                          Frota do Mecânico — Sem mão de obra / Peças 50/50
                        </span>
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-2 rounded-full ${
                          alerta.urgencia === 'ATRASADO' ? 'bg-red-300' :
                          alerta.urgencia === 'CRÍTICO' ? 'bg-red-200' : 'bg-orange-200'
                        }`}>
                          <AlertTriangle className={`h-4 w-4 ${
                            alerta.urgencia === 'ATRASADO' || alerta.urgencia === 'CRÍTICO' 
                              ? 'text-red-600' : 'text-orange-600'
                          }`} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{alerta.moto_placa}</p>
                          <p className="text-xs text-gray-500">{alerta.moto_modelo}</p>
                        </div>
                      </div>
                      <Badge className={`${
                        alerta.urgencia === 'ATRASADO' 
                          ? 'bg-red-700 text-white' 
                          : alerta.urgencia === 'CRÍTICO'
                            ? 'bg-red-500 text-white' 
                            : 'bg-orange-500 text-white'
                      }`}>
                        {alerta.km_restantes <= 0 
                          ? `${Math.abs(alerta.km_restantes)} km ATRASADO` 
                          : `${alerta.km_restantes} km restantes`}
                      </Badge>
                    </div>

                    <div className="space-y-2 text-sm">
                      {/* BADGE DO PLANO EM DESTAQUE */}
                      {alerta.tipo_plano && (
                        <div className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 font-bold text-sm ${
                          alerta.tipo_plano.toLowerCase() === 'fidelidade'
                            ? 'bg-emerald-100 border-2 border-emerald-400 text-emerald-800'
                            : alerta.tipo_plano.toLowerCase() === 'anual'
                              ? 'bg-blue-100 border-2 border-blue-400 text-blue-800'
                              : 'bg-purple-100 border-2 border-purple-400 text-purple-800'
                        }`}>
                          <Settings className="h-4 w-4" />
                          PLANO {alerta.tipo_plano.toUpperCase()}
                          {alerta.tipo_plano.toLowerCase() === 'fidelidade' && ' — Locadora'}
                          {(alerta.tipo_plano.toLowerCase() === 'anual' || alerta.tipo_plano.toLowerCase() === 'mensal') && ' — Só Óleo'}
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-500">KM Atual:</span>
                        <span className="font-medium">{alerta.km_atual.toLocaleString()} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Próx. Manutenção:</span>
                        <span className="font-medium">{alerta.proxima_manutencao_km.toLocaleString()} km</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Cliente:</span>
                        <span className="font-medium">{alerta.cliente_nome}</span>
                      </div>
                    </div>

                    {/* Botões de ação */}
                    <div className="mt-4 space-y-2">
                      {/* Botão de enviar WhatsApp ao cliente */}
                      {alerta.cliente_telefone && (
                        <Button
                          onClick={() => abrirWhatsApp(alerta)}
                          className="w-full bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`btn-whatsapp-${alerta.moto_id}`}
                        >
                          <Phone className="h-4 w-4 mr-2" />
                          Enviar Aviso ao Cliente
                        </Button>
                      )}

                      {/* Botão de notificar mecânico */}
                      <Button
                        onClick={() => notificarMecanico(alerta)}
                        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid={`btn-mecanico-${alerta.moto_id}`}
                      >
                        <Wrench className="h-4 w-4 mr-2" />
                        Notificar Mecânico
                      </Button>

                      {/* Botão de confirmar notificação */}
                      <Button
                        onClick={() => confirmarNotificacao(alerta.moto_id)}
                        disabled={confirmandoId === alerta.moto_id}
                        variant="outline"
                        className="w-full border-green-500 text-green-700 hover:bg-green-50"
                        data-testid={`btn-confirmar-${alerta.moto_id}`}
                      >
                        {confirmandoId === alerta.moto_id ? (
                          <>Confirmando...</>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-2" />
                            Confirmar que Notifiquei o Cliente
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dica */}
            <div className="p-4 border-t bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Clique em &quot;Enviar Aviso WhatsApp&quot; para notificar o cliente, 
                depois confirme que notificou para encerrar o alerta.
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AlertaManutencaoGlobal;
